import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Padding = () => {
  return (
    <div className="tutorial-container">
      {/* Title */}
      <h1 className="tutorial-title">📦 CSS Padding</h1>

      {/* Intro */}
      <p className="tutorial-intro">
        The <code>padding</code> property in CSS is used to create space
        <strong> inside an element</strong>, between the element’s content and
        its border. Unlike margins, padding is part of the element’s box and
        takes the background color of the element.
      </p>

      {/* Syntax */}
      <div className="tutorial-section">
        <h2 className="section-title">Syntax</h2>
        <CodeCopyBlock
          code={`selector {
  padding: value;         /* all sides */
  padding-top: value;     /* top padding */
  padding-right: value;   /* right padding */
  padding-bottom: value;  /* bottom padding */
  padding-left: value;    /* left padding */
}`}
        />
      </div>

      {/* Example */}
      <div className="tutorial-section">
        <h2 className="section-title">Example</h2>
        <CodeCopyBlock
          code={`div {
  border: 2px solid #333;
  padding: 20px;
}`}
        />
        <div
          style={{
            border: "2px solid #333",
            padding: "20px",
            background: "#f1f5f9",
          }}
        >
          I have <strong>20px padding</strong> on all sides.
        </div>
      </div>

      {/* Individual Padding */}
      <div className="tutorial-section">
        <h2 className="section-title">Individual Padding</h2>
        <p>
          You can set different padding values for each side using{" "}
          <code>padding-top</code>, <code>padding-right</code>,{" "}
          <code>padding-bottom</code>, and <code>padding-left</code>.
        </p>
        <CodeCopyBlock
          code={`div {
  padding-top: 10px;
  padding-right: 20px;
  padding-bottom: 30px;
  padding-left: 40px;
}`}
        />
        <div
          style={{
            border: "2px solid #15803d",
            paddingTop: "10px",
            paddingRight: "20px",
            paddingBottom: "30px",
            paddingLeft: "40px",
            background: "#dcfce7",
          }}
        >
          Different padding applied on each side.
        </div>
      </div>

      {/* Padding Shorthand */}
      <div className="tutorial-section">
        <h2 className="section-title">Padding Shorthand</h2>
        <p>
          Like margins, the <code>padding</code> property supports shorthand:
        </p>
        <ul>
          <li>
            <strong>1 value:</strong> all sides equal
          </li>
          <li>
            <strong>2 values:</strong> top & bottom | left & right
          </li>
          <li>
            <strong>3 values:</strong> top | left & right | bottom
          </li>
          <li>
            <strong>4 values:</strong> top | right | bottom | left
          </li>
        </ul>
        <CodeCopyBlock
          code={`div { padding: 20px; }         /* all sides 20px */
div { padding: 10px 30px; }    /* top/bottom 10px, left/right 30px */
div { padding: 5px 20px 15px; } /* top 5px, left/right 20px, bottom 15px */
div { padding: 10px 15px 20px 25px; } /* top | right | bottom | left */`}
        />
      </div>

      {/* Padding vs Margin */}
      <div className="tutorial-section">
        <h2 className="section-title">Padding vs Margin</h2>
        <p>
          - <strong>Padding</strong> adds space <em>inside</em> the element’s
          border. - <strong>Margin</strong> adds space <em>outside</em> the
          element’s border.
        </p>
        <div style={{ display: "flex", gap: "20px", flexWrap: "wrap" }}>
          <div
            style={{
              border: "2px solid #0ea5e9",
              padding: "20px",
              background: "#e0f2fe",
              width: "200px",
              textAlign: "center",
            }}
          >
            Padding Example
          </div>
          <div
            style={{
              border: "2px solid #f59e0b",
              margin: "20px",
              background: "#fef3c7",
              width: "200px",
              textAlign: "center",
            }}
          >
            Margin Example
          </div>
        </div>
      </div>

      {/* Next Steps */}
      <div className="tutorial-section">
        <h2 className="section-title">Next Steps</h2>
        <p>
          After learning margins and padding, you should check out{" "}
          <a href="/css/boxmodel" className="learn-more">
            CSS Box Model
          </a>{" "}
          to understand how margins, borders, padding, and content work together
          to define an element’s space.
        </p>
      </div>
    </div>
  );
};

export default CSS_Padding;
